import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Button, Heading } from '@paro.io/base-ui'

import { FURLOUGH_CODE_OPTIONS } from '../../../shared/constants/paroReferrals'
import { useQueryParams } from '../../../shared/customHooks/useQueryParams'
import ApplicationInfoModal from './ApplicationInfoModal'
import Business from './Business'
import CurrentResident from './CurrentResident'
import Header from './Header'
import { calcPasswordStrength, checkDuplicateEmail, validationSchema } from './service'
import User from './User'
import TermsOfUse from './TermsOfUse'
import Benefits from './Benefits'
import LinkedInCreateAccount from './LinkedInCreateAccount'
import { Box, Paper, Typography } from '@material-ui/core'

interface CreateAccountProps {
  history: any
  isFurloughReferral: boolean
  setIsFirm: (isFirm: boolean) => void
  isByob?: boolean;
}
declare var window: any

const CreateAccount = ({ history, isFurloughReferral, setIsFirm, isByob = false }: CreateAccountProps) => {
  const query = useQueryParams()
  const [isCurrentResident, setIsCurrentResident] = useState<boolean | null>(true)
  const [isLoading, setIsLoading] = useState(false)
  const furloughSource = query.get('source')?.toLowerCase()
  const isLinkedInAuthEnabled = process.env.REACT_APP_LINKEDIN_AUTH_ENABLED === 'true'
  const applicationsDisabled = process.env.REACT_APP_APPLICATIONS_DISABLED === 'true'

  useEffect(() => {
    localStorage.removeItem("email");
    localStorage.removeItem("userId");
  }, []);

  const isCustomReferral =
    furloughSource === 'join-paro-becker' ||
    furloughSource === 'join-paro-ladders' ||
    furloughSource === 'join-paro-cpa-directory' ||
    furloughSource === 'mycpe' ||
    furloughSource === 'thedailycpa' ||
    furloughSource === 'join-paro-cfo-dive' ||
    furloughSource === 'join-paro-american-alliance-mergers-acquisitions' ||
    furloughSource === 'join-paro-accounting-today'

  const getFurloughCode = () => {
    switch (furloughSource) {
      case 'join-paro-becker':
        return 'Becker'
      case 'join-paro-ladders':
        return 'Ladders'
      case 'join-paro-cpa-directory':
        return 'CPADirectory'
      case 'join-paro-going-concern':
        return 'Going Concern'
      case 'mycpe':
        return 'myCPE'
      case 'thedailycpa':
        return 'The Daily CPA'
      case 'cpethink':
        return 'CPEThink'
      case 'join-paro-cfo-dive':
        return 'CFO Dive'
      case 'join-paro-american-alliance-mergers-acquisitions':
        return 'American Alliance For Mergers and Acquisitions'
      case 'join-paro-accounting-today':
        return 'Accounting Today'
      case 'join-paro-accounting-today-cas-summit-2022':
        return 'Accounting Today CAS Summit 2022'
      default:
        return ''
    }
  }

  const furloughCode = getFurloughCode()

  return (
    <Formik
      validateOnBlur={false}
      initialValues={{
        acceptedTerms: false,
        companyName: '',
        confirmPassword: '',
        email: '',
        firstName: '',
        furloughCode,
        isCompany: false,
        isFurloughReferral: isFurloughReferral,
        lastName: '',
        password: '',
        registrationSource: furloughSource,
        isFirmProfile: false,
        showError: false,
      }}
      validationSchema={validationSchema}
      isInitialValid={false}
      onSubmit={async (values, actions) => {
        if (applicationsDisabled) return;
        
        setIsLoading(true)
        const { firstName, lastName, email, password, confirmPassword, companyName, furloughCode, registrationSource, isFirmProfile } = values
        const matchingFurloughCodeOption = FURLOUGH_CODE_OPTIONS.find(
          (furloughCodeOption) => furloughCodeOption.label === furloughCode,
        )
        window?.heap?.identify(email)

        if (password !== confirmPassword) {
          actions.setFieldError('password', 'Passwords must match')
          actions.setFieldError('confirmPassword', 'Passwords must match')
          actions.setSubmitting(false)
          setIsLoading(false)
          return
        }

        const { valid, message } = await checkDuplicateEmail(email)

        if (!valid) {
          actions.setFieldError('email', message)
          actions.setSubmitting(false)
          setIsLoading(false)
          return
        }
        if (isFirmProfile) {
          setIsFirm?.(true)
        }

        const payload = {
          freelancer: {
            firstName,
            lastName,
            email,
            password,
            companyName,
            furloughCode: matchingFurloughCodeOption?.value || '',
            registrationSource,
            isFirmProfile,
            isByob: isByob,
          },
        }

        axios
          .post(`${process.env.REACT_APP_V3_ENDPOINT}/expert-profiles/expert-user`, payload)
          .then((response) => {
            if (response.status === 200) {
              setIsLoading(false)
              if (isByob) {
                localStorage.setItem("email", email);
                // Defensive extraction and logging
                const freelancers = response.data?.message?.freelancers;
                let freelancerId = null;
                if (Array.isArray(freelancers) && freelancers.length > 0) {
                  freelancerId = freelancers[0]?.legacyFreelancerId;
                }
                localStorage.setItem("userId", freelancerId !== null && freelancerId !== undefined ? String(freelancerId) : "");
                history.push(`/registration/aigp-onboarding/quickbooks`)
              } else {
                history.push(`/registration/verify/${email}`)
              }
            }
          })
          .catch((error) => {
            console.log('Error creating user', error)
            const context = {
              variables: JSON.stringify(payload),
              path: `${process.env.REACT_APP_V3_ENDPOINT}/expert-profiles/expert-user`,
            }
            Sentry.captureException(`Error creating user: ${error}`, {
              contexts: { context },
            })
          })
      }}
    >
      {(props) => {
        const {
          isSubmitting,
          isValidating,
          setFieldValue,
          submitForm,
          values: { acceptedTerms, companyName, firstName, isCompany, lastName, password, isFirmProfile, showError },
        } = props

        const { strength, strengthMsg } = calcPasswordStrength(password)
        const strongPassword = strength >= 75

        const formStatus = !isValidating && !isSubmitting && strongPassword
        const validateSubmit = formStatus && !!isCurrentResident && !acceptedTerms

        const handleSubmitFn = (): void => {
          if (applicationsDisabled) return;
          
          if (validateSubmit) {
            setFieldValue('showError', true)
          } else {
            submitForm()
          }
        }

        return (
          <Form>
            {!isByob && <Header />}
            {applicationsDisabled && (<Paper elevation={0} className="container mb-6 p-6 bg-red-50 border border-red-100">
              <Typography variant="h6" className="mb-2 font-bold">
                Applications Temporarily Paused
              </Typography>
              <Typography variant="body1">
                Thank you for your interest in joining the Paro expert network. At this time, we've paused onboarding new finance professionals as we continue to support our current network and client needs. We're excited to reopen applications in the future. If you'd like to be notified when applications resume, please check back soon or follow us on{' '}
                <a href="https://www.linkedin.com/company/paro-ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  LinkedIn
                </a>{' '}
                for updates.
              </Typography>
            </Paper>)}
            <div className="container flex">
              <div className={`pr-0 md:pr-20  ${isByob ? "w-full" : "md:w-2/3 xs:w-full md:border-r-2 border-red-100"}`}>
                <Heading size="h6" className="mb-1">
                  CREATE YOUR ACCOUNT
                </Heading>
                <CurrentResident {...{ isCurrentResident, handleChange: setIsCurrentResident }} />
                <Business {...{ isCurrentResident, setFieldValue, isFirmProfile }} />
                {isLinkedInAuthEnabled && !isByob && <LinkedInCreateAccount {...{ companyName, isCompany, isCurrentResident, disabled: applicationsDisabled }} />}
                <User
                  {...{
                    isCurrentResident,
                    isFurloughReferral,
                    password,
                    setFieldValue,
                    strength,
                    strengthMsg,
                    isCustomReferral,
                  }}
                />
                <TermsOfUse {...{ companyName, firstName, isCompany, lastName, setFieldValue, acceptedTerms, showError, isByob }} />
                <Box>
                  <Button
                    label="Continue Registration"
                    id="continueRegistrationButton"
                    color="primary"
                    onClick={handleSubmitFn}
                    className={`w-full mt-2`}
                    isLoading={isLoading}
                    disabled={applicationsDisabled}
                  />
                </Box>
              </div>
              {!isByob && <div>
                <Benefits />
              </div>}
            </div>
            <div style={{ paddingTop: '2.5%' }} />
            <ApplicationInfoModal {...{ isCurrentResident, handleChange: setIsCurrentResident }} />
          </Form>
        )
      }}
    </Formik>
  )
}

export default CreateAccount
